import { Tooltip } from '@nextui-org/react'
import React from 'react'

export interface CustomTooltipProps {
  /** The content to display inside the tooltip */
  content: React.ReactNode
  /** The trigger element that will show the tooltip on hover */
  children: React.ReactNode
  /** Placement of the tooltip relative to trigger */
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /** Custom class names for the tooltip */
  classNames?: {
    base?: string
    content?: string
  }
  /** Whether to show the arrow */
  showArrow?: boolean
  /** Whether the tooltip is disabled */
  isDisabled?: boolean
  /** Controlled open state for cases that need custom trigger timing */
  isOpen?: boolean
  /** Delay before showing the tooltip after pointer/focus enters */
  delay?: number
  /** Delay before hiding the tooltip after pointer/focus leaves */
  closeDelay?: number
}

/**
 * A generic tooltip component with consistent styling that wraps any element.
 * The tooltip content can be any ReactNode, making it fully dynamic.
 *
 * @example
 * ```tsx
 * <CustomTooltip
 *   content={<div>Custom tooltip content</div>}
 *   placement="right"
 * >
 *   <button>Hover me</button>
 * </CustomTooltip>
 * ```
 */
export const CustomTooltip: React.FC<CustomTooltipProps> = ({
  content,
  children,
  placement = 'right',
  classNames,
  showArrow = true,
  isDisabled = false,
  isOpen,
  delay,
  closeDelay
}) => {
  return (
    <Tooltip
      content={content}
      placement={placement}
      showArrow={showArrow}
      isDisabled={isDisabled}
      isOpen={isOpen}
      delay={delay}
      closeDelay={closeDelay}
      classNames={{
        base: `before:!bg-[#FFFFFF] before:!border-transparent before:![box-shadow:none] ${classNames?.base || ''}`,
        content: `!bg-[#FFFFFF] !border-0 rounded-[16px] p-2 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.02),0px_1px_6px_-1px_rgba(0,0,0,0.02),0px_1px_2px_0px_rgba(0,0,0,0.03)] select-none ${classNames?.content || ''}`
      }}
    >
      {children}
    </Tooltip>
  )
}

export default CustomTooltip
